import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import PlanBadge, { PLANS } from "../../components/PlanBadge";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Search, MoreVertical, Pause, Play, ArrowRightLeft, ShoppingBag, LogIn, Building2, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/utils";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [moveOpen, setMoveOpen] = useState(null);
  const [newDealer, setNewDealer] = useState("");
  const [commercialOpen, setCommercialOpen] = useState(null);
  const [commercialData, setCommercialData] = useState(null);
  const [commercialBusy, setCommercialBusy] = useState(false);
  const [domainOpen, setDomainOpen] = useState(null);
  const [domainValue, setDomainValue] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);
  const [commercialForm, setCommercialForm] = useState({ monthly_rate_per_flat: 0, billing_frequency: "yearly", billable_flat_count: 0, start_date: new Date().toISOString().slice(0, 10), grace_days: 7, notes: "" });

  const load = async () => {
    try {
      const [c, d] = await Promise.all([api.get("/admin/clients"), api.get("/admin/dealers")]);
      setClients(c.data); setDealers(d.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (c, status) => {
    try { await api.put(`/admin/clients/${c.id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const move = async () => {
    try { await api.put(`/admin/clients/${moveOpen.id}/move`, { new_dealer_id: newDealer }); toast.success("Client moved"); setMoveOpen(null); setNewDealer(""); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const toggleEcommerce = async (c) => {
    try {
      await api.put(`/admin/clients/${c.id}/ecommerce`, { enabled: !c.ecommerce_enabled });
      toast.success(`Ecommerce ${!c.ecommerce_enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const toggleBasicMenuLevel2 = async (c) => {
    try {
      await api.put(`/admin/clients/${c.id}/basic-menu-level2`, { enabled: !c.basic_menu_level2_enabled });
      toast.success(`Basic Menu Level 2 ${!c.basic_menu_level2_enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openDomain = (c) => {
    setDomainOpen(c);
    setDomainValue(c.custom_booking_domain || "");
  };

  const saveDomain = async () => {
    setDomainBusy(true);
    try {
      const { data } = await api.put(`/admin/clients/${domainOpen.id}/custom-domain`, { custom_booking_domain: domainValue.trim() });
      const status = data?.custom_booking_domain_status || "none";
      toast.success(status === "active" ? "Custom domain is live" : domainValue.trim() ? "Domain saved; DNS/SSL status updated" : "Custom domain removed");
      setDomainOpen(null); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail || e)); }
    finally { setDomainBusy(false); }
  };

  const loginAsClient = async (c) => {
    if (!window.confirm(`Open ${c.name}'s client panel in a new tab?`)) return;
    try {
      const { data } = await api.post(`/admin/clients/${c.id}/login-as`);
      const url = `/switch-login?token=${encodeURIComponent(data.access_token || "")}&to=/client`;
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) {
        toast.error("Popup blocked. Please allow popups for this site and try again.");
        return;
      }
      toast.success(`Opening ${data.user?.name || c.name}'s client panel`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openCommercial = async (c) => {
    setCommercialOpen(c); setCommercialData(null);
    try {
      const { data } = await api.get(`/admin/clients/${c.id}/society-commercial`);
      setCommercialData(data);
      setCommercialForm({
        monthly_rate_per_flat: data.commercial?.monthly_rate_per_flat ?? data.suggested_monthly_rate ?? 0,
        billing_frequency: data.commercial?.billing_frequency || "yearly",
        billable_flat_count: data.commercial?.billable_flat_count ?? data.active_flat_count ?? 0,
        start_date: new Date().toISOString().slice(0, 10),
        grace_days: data.commercial?.grace_days ?? 7,
        notes: "",
      });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setCommercialOpen(null); }
  };

  const activateCommercial = async () => {
    setCommercialBusy(true);
    try {
      await api.post(`/admin/clients/${commercialOpen.id}/society-commercial/activate`, { ...commercialForm, monthly_rate_per_flat: Number(commercialForm.monthly_rate_per_flat), billable_flat_count: Number(commercialForm.billable_flat_count), grace_days: Number(commercialForm.grace_days) });
      toast.success("Society operational subscription activated");
      await openCommercial(commercialOpen); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setCommercialBusy(false); }
  };

  const changeCommercialStatus = async (status) => {
    setCommercialBusy(true);
    try {
      await api.post(`/admin/clients/${commercialOpen.id}/society-commercial/status`, { status, notes: commercialForm.notes || "" });
      toast.success(`Society subscription ${status}`); await openCommercial(commercialOpen); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setCommercialBusy(false); }
  };

  const cycleMonths = { monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12 };
  const commercialTotal = Number(commercialForm.billable_flat_count || 0) * Number(commercialForm.monthly_rate_per_flat || 0) * cycleMonths[commercialForm.billing_frequency];

  const filtered = clients.filter((c) => {
    const m = filter === "all" || c.plan === filter;
    const s = !search || `${c.name} ${c.organization_name || ""} ${c.email} ${c.gst_number} ${c.dealer_name}`.toLowerCase().includes(search.toLowerCase());
    return m && s;
  });

  return (
    <div data-testid="admin-clients-page">
      <PageHeader overline="Admin / Clients" title="All clients." subtitle="View every client. Suspend, activate or move them between dealers." />

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients..." className="pl-9 rounded-sm border-[#E5E7EB]" data-testid="search-client" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 rounded-sm border-[#E5E7EB]" data-testid="filter-client-plan"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">Client Code</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">Client</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">Dealer</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">GST</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">Plan</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">Status</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280] text-right">Wallet</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#6B7280]">No clients found.</TableCell></TableRow>}
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`admin-client-row-${c.id}`}>
                <TableCell className="font-mono font-semibold text-[#111827]">{c.client_code || "—"}</TableCell>
                <TableCell>
                  <div className="font-semibold">{c.name}</div>
                  {c.organization_name && <div className="text-xs text-[#374151]">{c.organization_name}</div>}
                  <div className="text-xs text-[#6B7280]">{c.email}</div>
                  {c.vertical === "retail_store" && (
                    <div className={`mt-1 inline-flex rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${c.ecommerce_enabled ? "bg-emerald-50 text-emerald-700" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                      Ecommerce {c.ecommerce_enabled ? "ON" : "OFF"}
                    </div>
                  )}
                  {c.vertical === "society" && (
                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]">
                      Society service: {c.society_commercial_state?.effective_status || c.society_commercial_state?.operational_status || "legacy active"} · {c.society_active_flat_count || 0} flats
                    </div>
                  )}
                  {c.vertical === "retailer" && c.restaurant_mode === "menu_only" && <div className={`mt-1 inline-flex rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${c.basic_menu_level2_enabled ? "bg-emerald-50 text-emerald-700" : "bg-[#F3F4F6] text-[#6B7280]"}`}>Basic Menu Level 2 {c.basic_menu_level2_enabled ? "ON" : "OFF"}</div>}
                </TableCell>
                <TableCell className="text-sm">{c.dealer_name || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{c.gst_number || "—"}</TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {c.plan_id ? (
                      <>
                        <div className="font-semibold text-sm">{c.plan_name || c.plan || "Subscription"}</div>
                        <div className="text-xs text-[#6B7280]">Start {formatDateTime(c.plan_started_at || c.created_at)}</div>
                        <div className="text-xs text-[#6B7280]">End {formatDateTime(c.plan_expires_at || c.subscription_expires_at)}</div>
                      </>
                    ) : (
                      <PlanBadge plan={c.plan} />
                    )}
                  </div>
                </TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell className="text-right font-mono">₹ {Number(c.wallet_balance || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`client-menu-${c.id}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem onClick={() => loginAsClient(c)} data-testid={`login-as-client-${c.id}`}>
                        <LogIn className="w-3.5 h-3.5 mr-2" /> Login as Client
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openDomain(c)} data-testid={`custom-domain-${c.id}`}>
                        <Globe2 className="w-3.5 h-3.5 mr-2" /> Manage Custom Domain
                      </DropdownMenuItem>
                      {c.vertical === "society" && (
                        <DropdownMenuItem onClick={() => openCommercial(c)} data-testid={`society-commercial-${c.id}`}>
                          <Building2 className="w-3.5 h-3.5 mr-2" /> Society Commercial Setup
                        </DropdownMenuItem>
                      )}
                      {c.status === "active" ? (
                        <DropdownMenuItem onClick={() => setStatus(c, "suspended")} data-testid={`suspend-${c.id}`}><Pause className="w-3.5 h-3.5 mr-2" />Suspend</DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => setStatus(c, "active")} data-testid={`activate-${c.id}`}><Play className="w-3.5 h-3.5 mr-2" />Activate</DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => { setMoveOpen(c); setNewDealer(""); }} data-testid={`move-${c.id}`}><ArrowRightLeft className="w-3.5 h-3.5 mr-2" />Move to dealer</DropdownMenuItem>
                      {c.vertical === "retail_store" && (
                        <DropdownMenuItem onClick={() => toggleEcommerce(c)} data-testid={`ecommerce-${c.id}`}>
                          <ShoppingBag className="w-3.5 h-3.5 mr-2" />{c.ecommerce_enabled ? "Disable ecommerce" : "Enable ecommerce"}
                        </DropdownMenuItem>
                      )}
                      {c.vertical === "retailer" && c.restaurant_mode === "menu_only" && <DropdownMenuItem onClick={() => toggleBasicMenuLevel2(c)} data-testid={`basic-menu-level2-${c.id}`}><ShoppingBag className="mr-2 h-3.5 w-3.5" />{c.basic_menu_level2_enabled ? "Disable Basic Menu Level 2" : "Enable Basic Menu Level 2"}</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!moveOpen} onOpenChange={(o) => !o && setMoveOpen(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Move client</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280]">Move <strong>{moveOpen?.name}</strong> from <strong>{moveOpen?.dealer_name}</strong> to another dealer. Assigned templates will be cleared.</p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">New dealer</Label>
            <Select value={newDealer} onValueChange={setNewDealer}>
              <SelectTrigger className="rounded-sm" data-testid="new-dealer-select"><SelectValue placeholder="Select dealer" /></SelectTrigger>
              <SelectContent>
                {dealers.filter((d) => d.id !== moveOpen?.dealer_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={move} disabled={!newDealer} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white" data-testid="move-submit">Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!domainOpen} onOpenChange={(o) => !o && setDomainOpen(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold">Custom booking domain</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-[110px_1fr] overflow-hidden rounded-sm border bg-white text-xs">
              <div className="border-b border-r bg-[#F3F4F6] px-3 py-2 font-semibold">Salon</div>
              <div className="border-b px-3 py-2 font-semibold">{domainOpen?.name}</div>
              <div className="border-r bg-[#F3F4F6] px-3 py-2 font-semibold">Status</div>
              <div className="px-3 py-2 capitalize">{String(domainOpen?.custom_booking_domain_status || "not set").replace(/_/g, " ")}</div>
            </div>
            {domainOpen?.custom_booking_domain_error ? <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">{domainOpen.custom_booking_domain_error}</div> : null}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Custom booking domain</Label>
              <Input value={domainValue} onChange={(e) => setDomainValue(e.target.value)} placeholder="beautysalon.com or book.beautysalon.com" className="mt-1 rounded-sm font-mono" data-testid="admin-custom-domain-input" />
              <div className="mt-2 overflow-hidden rounded-sm border border-[#D1D5DB] text-xs">
                <div className="grid grid-cols-[90px_1fr] border-b"><span className="bg-[#F3F4F6] px-2 py-1.5 font-semibold">Subdomain</span><span className="px-2 py-1.5">CNAME → <span className="font-mono">booking.rpsignage.com</span></span></div>
                <div className="grid grid-cols-[90px_1fr]"><span className="bg-[#F3F4F6] px-2 py-1.5 font-semibold">Root domain</span><span className="px-2 py-1.5">ALIAS/ANAME to the target, or A record to the production IP</span></div>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-[#6B7280]">Saving verifies DNS and provisions HTTPS automatically. The branded address remains visible while the existing booking account is loaded. Leave blank to disconnect it.</p>
              <p className="mt-2 text-xs font-semibold text-red-700">RP Signage domains and their subdomains are permanently reserved and cannot be assigned.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDomainOpen(null)} disabled={domainBusy}>Cancel</Button>
            <Button onClick={saveDomain} disabled={domainBusy} className="bg-[#111827] text-white" data-testid="admin-custom-domain-save">{domainBusy ? "Checking…" : "Save & Verify"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!commercialOpen} onOpenChange={(o) => !o && setCommercialOpen(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Society Commercial Setup</DialogTitle></DialogHeader>
          {!commercialData ? <p className="py-8 text-center text-sm text-[#6B7280]">Loading commercial details…</p> : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-sm border bg-[#F9FAFB] p-4 text-sm">
                <div><div className="text-xs text-[#6B7280]">Setup licence</div><div className={`font-semibold ${commercialData.commercial?.onboarding_status === "active" ? "text-emerald-700" : "text-amber-700"}`}>{commercialData.commercial?.onboarding_status === "active" ? "Active" : "Payment pending"}</div></div>
                <div><div className="text-xs text-[#6B7280]">Operational status</div><div className="font-semibold">{commercialData.commercial?.effective_status || commercialData.commercial?.operational_status || "—"}</div></div>
                <div><div className="text-xs text-[#6B7280]">Active flats</div><div className="font-semibold">{commercialData.active_flat_count}</div></div>
                <div><div className="text-xs text-[#6B7280]">Suggested rate</div><div className="font-semibold">₹{Number(commercialData.suggested_monthly_rate || 0).toLocaleString()}/flat/month</div></div>
              </div>
              {commercialData.commercial?.onboarding_status !== "active" && (
                <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <div className="font-semibold">The green client status is the signage plan, not the Society setup licence.</div>
                  <div className="mt-1">The assigned dealer must open Dealer → My Clients → this Society's three-dot menu → <b>Activate Lifetime Setup</b>. The setup fee is deducted from the dealer wallet; then reopen this dialog to activate the operational subscription.</div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider text-[#6B7280]">Billable flats</Label><Input type="number" min="1" value={commercialForm.billable_flat_count} onChange={(e) => setCommercialForm({ ...commercialForm, billable_flat_count: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#6B7280]">Rate per flat/month (₹)</Label><Input type="number" min="0" step="0.01" value={commercialForm.monthly_rate_per_flat} onChange={(e) => setCommercialForm({ ...commercialForm, monthly_rate_per_flat: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#6B7280]">Collection frequency</Label><Select value={commercialForm.billing_frequency} onValueChange={(v) => setCommercialForm({ ...commercialForm, billing_frequency: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="half_yearly">Six monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent></Select></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#6B7280]">Start date</Label><Input type="date" value={commercialForm.start_date} onChange={(e) => setCommercialForm({ ...commercialForm, start_date: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#6B7280]">Grace period (days)</Label><Input type="number" min="0" max="90" value={commercialForm.grace_days} onChange={(e) => setCommercialForm({ ...commercialForm, grace_days: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#6B7280]">Admin note</Label><Input value={commercialForm.notes} onChange={(e) => setCommercialForm({ ...commercialForm, notes: e.target.value })} placeholder="Contract or renewal note" /></div>
              </div>
              <div className="rounded-sm border border-blue-200 bg-blue-50 p-4"><div className="text-xs uppercase tracking-wider text-blue-700">Subscription calculation</div><div className="mt-1 text-xl font-bold">{Number(commercialForm.billable_flat_count || 0)} × ₹{Number(commercialForm.monthly_rate_per_flat || 0).toLocaleString()} × {cycleMonths[commercialForm.billing_frequency]} = ₹{commercialTotal.toLocaleString()}</div></div>
              <DialogFooter className="gap-2">
                {commercialData.commercial?.operational_status === "active" && <Button variant="outline" disabled={commercialBusy} onClick={() => changeCommercialStatus("suspended")}>Suspend</Button>}
                {commercialData.commercial?.operational_status === "suspended" && <Button variant="outline" disabled={commercialBusy} onClick={() => changeCommercialStatus("active")}>Resume current contract</Button>}
                <Button title={commercialData.commercial?.onboarding_status !== "active" ? "Dealer must activate Lifetime Setup first" : ""} disabled={commercialBusy || commercialData.commercial?.onboarding_status !== "active" || Number(commercialForm.billable_flat_count) < 1} onClick={activateCommercial} className="bg-[#111827] text-white">{commercialData.commercial?.contract_id ? "Renew / replace contract" : "Activate subscription"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
